import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { DocusignTemplate } from 'src/interfaces/docusignTemplate'

interface Props {
  isOpen: boolean
  template: DocusignTemplate | null
  isDeleting: boolean
  onConfirm(): void
  onClose(): void
}

const DeleteDocusignTemplateModalComponent = ({
  isOpen,
  template,
  isDeleting,
  onConfirm,
  onClose,
}: Props) => {
  if (!template) {
    return null
  }

  // `title` is nullable in the DB, so the sentence needs a fallback subject rather than an
  // empty gap. This is a cosmetic default, not fabricated content.
  const templateLabel = template.title?.trim() || 'This template'

  return (
    <CommonModal
      isOpen={isOpen}
      title="Delete this DocuSign template?"
      onClose={onClose}
      buttons={
        <Fragment>
          {/* Disabled while the request is in flight so the control matches the page's
              dismissal guard — a Cancel that looks clickable but does nothing is worse. */}
          <Button size="small" color="grey" disabled={isDeleting} onClick={onClose}>
            Cancel
          </Button>
          {/* `red` is the shared Button's destructive variant. The colour carries the warning
              alongside the title, body and label. */}
          <Button
            size="small"
            color="red"
            loading={isDeleting}
            disabled={isDeleting}
            onClick={onConfirm}
            data-testid="docusign-template-delete-confirm-button"
          >
            Delete template
          </Button>
        </Fragment>
      }
    >
      <div css={tw`flex flex-col gap-2`}>
        {/* The backend performs a hard DELETE — this row has no `deletedAt` and nothing
            restores it. See the design's §2.5. */}
        <Typography variant="body2">
          {templateLabel} will be permanently deleted. This cannot be undone.
        </Typography>
        <Typography variant="body2" css={tw`text-shades-500`}>
          Onboarding steps that sign this template will stop working, and a template an insurance
          policy product still uses cannot be deleted — remove it from those products first.
        </Typography>
      </div>
    </CommonModal>
  )
}

export const DeleteDocusignTemplateModal = memo(DeleteDocusignTemplateModalComponent)
